<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';
import { usePolicy } from 'dashboard/composables/usePolicy';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  kind: {
    type: String,
    default: 'document_base',
  },
  documentsCount: {
    type: Number,
    default: 0,
  },
  responsesCount: {
    type: Number,
    default: 0,
  },
  updatedAt: {
    type: Number,
    default: null,
  },
  pendingEmbeddingsCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['action', 'navigate']);
const { checkPermissions } = usePolicy();

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const isAdmin = computed(() => checkPermissions(['administrator']));

const isQaBase = computed(() => props.kind === 'qa_base');

const kindLabel = computed(() =>
  isQaBase.value
    ? t('ASSISTANT.KNOWLEDGE_BASES.KIND.QA.LABEL')
    : t('ASSISTANT.KNOWLEDGE_BASES.KIND.DOCUMENT.LABEL')
);

const menuItems = computed(() => [
  {
    label: t('ASSISTANT.KNOWLEDGE_BASES.OPTIONS.DELETE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

// Р10 п.8: у qa-базы — счётчик вопрос-ответов; у документной — только
// документы (фрагменты индексации — техническая деталь, наружу не путаем).
const countsLabel = computed(() => {
  if (isQaBase.value) {
    return t('ASSISTANT.KNOWLEDGE_BASES.CARD.RESPONSES_COUNT', {
      count: props.responsesCount,
    });
  }
  return t('ASSISTANT.KNOWLEDGE_BASES.CARD.DOCUMENTS_COUNT', {
    count: props.documentsCount,
  });
});

// Р78: записи без вектора не находятся поиском — агент отвечает так, будто
// базы нет. Раньше это было видно только из консоли.
const hasPendingEmbeddings = computed(() => props.pendingEmbeddingsCount > 0);

// Р11 п.5: дата последнего изменения базы — dd.MM.yyyy, HH:mm.
const updatedAtLabel = computed(() =>
  props.updatedAt ? messageStamp(props.updatedAt, 'dd.MM.yyyy, HH:mm') : ''
);

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};

const handleNavigate = () => {
  emit('navigate', props.id);
};
</script>

<template>
  <CardLayout class="cursor-pointer" @click="handleNavigate">
    <div class="flex justify-between w-full gap-1">
      <div class="flex items-center gap-2 min-w-0">
        <h6 class="text-base font-normal text-n-slate-12 line-clamp-1">
          {{ name }}
        </h6>
        <span
          class="px-1.5 py-0.5 text-xs rounded-md shrink-0"
          :class="
            isQaBase
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-blue-3 text-n-blue-11'
          "
        >
          {{ kindLabel }}
        </span>
      </div>
      <div v-if="isAdmin" class="flex items-center gap-2" @click.stop>
        <div
          v-on-clickaway="() => toggleDropdown(false)"
          class="relative flex items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="mt-1 ltr:right-0 rtl:left-0 top-full"
            @action="handleAction($event)"
          />
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between w-full gap-4">
      <span class="text-sm truncate text-n-slate-11">
        {{ description }}
      </span>
      <div class="flex items-center gap-3 shrink-0">
        <span
          v-if="hasPendingEmbeddings"
          class="px-1.5 py-0.5 text-xs rounded-md shrink-0 bg-n-ruby-3 text-n-ruby-11"
          :title="$t('ASSISTANT.KNOWLEDGE_BASES.CARD.NOT_INDEXED_HINT')"
        >
          {{
            $t('ASSISTANT.KNOWLEDGE_BASES.CARD.NOT_INDEXED', {
              count: pendingEmbeddingsCount,
            })
          }}
        </span>
        <span class="text-sm text-n-slate-11 line-clamp-1">
          {{ countsLabel }}
        </span>
        <span
          v-if="updatedAtLabel"
          class="text-sm text-n-slate-11 line-clamp-1"
        >
          {{
            $t('ASSISTANT.KNOWLEDGE_BASES.CARD.UPDATED_AT', {
              date: updatedAtLabel,
            })
          }}
        </span>
      </div>
    </div>
  </CardLayout>
</template>
